import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { AppBarData } from "@/appBar/appBarDomain";
import { isMobileView } from "@/utils/screen";
import { Box, Grid } from "@/components-v2/Box";
import { Logo } from "@/components-v2/Logo";
import { ThemeToggleButton, UserMenu } from "@/components-v2/AppBar";
import { useTranslation } from "@/stores/RootStoreContext";
import { Icon } from "@/components-v2/Icon";

export type TitleTextProps = {
  title: TMessage;
  subtitle?: TMessage;
};

export const TitleText = observer(({ title, subtitle }: TitleTextProps) => {
  const { t } = useTranslation();
  return (
    <div className="truncate">
      <div className="font-semibold truncate">{t(title)}</div>
      {subtitle && <div className={"subtle truncate"}>{t(subtitle)}</div>}
    </div>
  );
});

export type LandingPageHeaderProps = {
  logoLinkUrl: string;
  title?: TMessage;
  companyContext?: TitleTextProps;
  userContext?: TitleTextProps;
  userMenu?: AppBarData;
};

export const LandingPageHeader = observer(
  ({ userMenu, companyContext, title, userContext, logoLinkUrl }: LandingPageHeaderProps) => {
    const { t } = useTranslation();

    const logoBox = (
      <Box alignItems={"center"} gap={"md"} className="truncate">
        <Logo linkUrl={logoLinkUrl} />
        {companyContext && <TitleText {...companyContext} />}
      </Box>
    );

    const titleBox = title ? (
      <Box alignItems="center" className="truncate" justifyContent="center">
        <Box
          alignItems="center"
          className="truncate bg-alt2-dimmed"
          rounded={"lg"}
          padding={"sm"}
          fontWeight="bold"
        >
          <Icon name={"file-text"} color={"info"} />
          <div className="truncate">{t(title)}</div>
        </Box>
      </Box>
    ) : undefined;

    const userBoxMobile = (
      <Box alignItems={"center"}>
        <ThemeToggleButton />
        {userMenu && <UserMenu {...userMenu} />}
      </Box>
    );

    const userBox = (
      <Box
        alignItems={"center"}
        gap={"md"}
        className="truncate"
        textAlign="right"
        justifyContent="end"
      >
        {userContext && <TitleText {...userContext} />}
        <ThemeToggleButton />
        {userMenu && <UserMenu {...userMenu} />}
      </Box>
    );

    return isMobileView() ? (
      <Box justifyContent={"space-between"} alignItems="center" borderBottom bg padding>
        {logoBox}
        {userBoxMobile}
      </Box>
    ) : titleBox ? (
      <Grid cols={3} borderBottom bg padding gap={"md"}>
        {logoBox}
        {titleBox}
        {userBox}
      </Grid>
    ) : (
      <Box justifyContent={"space-between"} alignItems={"center"} borderBottom bg padding>
        {logoBox}
        {userBox}
      </Box>
    );
  },
);
